import { useState, useEffect } from 'react';
import { client, accountsClient } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import { Modal } from "../ui/modal";
import OrderStatusManagerModal from './OrderStatusManagerModal';

interface OrderCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface OrderItem {
    productId: number;
    productName: string;
    quantity: number;
    price: number;
}

export default function OrderCreateModal({ isOpen, onClose, onSuccess }: OrderCreateModalProps) {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [statuses, setStatuses] = useState<any[]>([]);
    const [items, setItems] = useState<OrderItem[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<string>(''); // productId
    const [qty, setQty] = useState(1);

    // Status Manager
    const [isStatusManagerOpen, setIsStatusManagerOpen] = useState(false);

    // Form Data
    const [formData, setFormData] = useState({
        user_id: '',
        status_id: '',
        payment_mode_id: 1, // Default mock
        shipping_mode_id: 1, // Default mock
        email: '',
        mobile_number: '',
        comments: '',
        discount_amount: 0,
        transaction: 'cash'
    });

    // Fetch dependencies on mount
    useEffect(() => {
        if (isOpen) {
            fetchDependencies();
        }
    }, [isOpen]);

    const fetchDependencies = async () => {
        setLoading(true);
        try {
            const [usersRes, productsRes] = await Promise.all([
                accountsClient.getUsers() as any,
                client.getApiCatalogProducts() as any
            ]);

            // Normalize users data similar to UsersTable
            const userData = usersRes.data?.data || usersRes.data || {};
            let allUsers: any[] = [];
            if (userData.cms_users) allUsers = [...allUsers, ...userData.cms_users];
            if (userData.client_users) allUsers = [...allUsers, ...userData.client_users];
            if (Array.isArray(userData)) allUsers = [...allUsers, ...userData];
            if (Array.isArray(usersRes.data?.data?.data)) allUsers = [...allUsers, ...usersRes.data.data.data]; // Standard paginated

            // Deduplicate users by ID
            setUsers(Array.from(new Map(allUsers.map(u => [u.id, u])).values()));

            setProducts(productsRes.data?.data?.data || productsRes.data?.data || []);

            await fetchStatuses();
        } catch (err) {
            console.error('Failed to fetch dependencies:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStatuses = async () => {
        try {
            const statusRes = await client.getApiOrdersStatus() as any;
            const statusData = statusRes.data?.data || statusRes.data || [];
            setStatuses(Array.isArray(statusData) ? statusData : []);
        } catch (err) {
            console.error('Failed to fetch statuses:', err);
        }
    };

    const handleUserChange = (userId: string) => {
        const user = users.find(u => u.id === Number(userId));
        setFormData(prev => ({
            ...prev,
            user_id: userId,
            email: user?.email || '',
            mobile_number: user?.mobile_number || user?.phone || user?.first_name ? '0000000000' : ''
        }));
    };

    const addItem = () => {
        if (!selectedProduct) return;
        const prod = products.find(p => p.id === Number(selectedProduct));
        if (!prod) return;

        const newItem: OrderItem = {
            productId: prod.id,
            productName: prod.title || prod.name || 'Product',
            quantity: qty,
            price: Number(prod.price || 0)
        };

        setItems([...items, newItem]);
        setSelectedProduct('');
        setQty(1);
    };

    const removeItem = (index: number) => {
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    };

    const calculateTotal = () => {
        const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const discount = Number(formData.discount_amount);
        return subtotal - (subtotal * discount) / 100;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (items.length === 0) {
            Swal.fire({
                title: 'Warning',
                text: 'Please add at least one product',
                icon: 'warning'
            });
            return;
        }

        if (!formData.user_id) {
            Swal.fire({
                title: 'Warning',
                text: 'Please select a user',
                icon: 'warning'
            });
            return;
        }

        try {
            const user = users.find(u => u.id === Number(formData.user_id));

            const response = await client.postApiOrdersOrderCreate({
                postApiOrdersOrderCreateRequest: {
                    user_id: Number(formData.user_id),
                    status_id: Number(formData.status_id),
                    payment_mode_id: Number(formData.payment_mode_id),
                    shipping_mode_id: Number(formData.shipping_mode_id),
                    email: formData.email || user?.email || '',
                    mobile_number: String(formData.mobile_number || user?.mobile_number || user?.phone || ''),
                    comments: String(formData.comments || ''),
                    billing: {
                        name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username : 'Customer',
                        address_1: 'Store Address 123',
                        city: 'Dubai',
                        company: user?.company_name || '',
                        uae_id: '123456789',
                        save: 0
                    },
                    shipping: {
                        authorized_person: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username : 'Customer',
                        address_1: 'Store Address 123',
                        city: 'Dubai',
                        uae_id: '123456789',
                        save: 0
                    },
                    product_id: items.map(i => i.productId),
                    product_quantity: items.map(i => i.quantity),
                    discount_amount: Number(formData.discount_amount),
                    transaction: formData.transaction
                }
            });
            console.log('Order created successfully:', response);
            onSuccess();
            onClose();
            // Reset form
            setItems([]);
            setFormData({ ...formData, user_id: '', email: '', mobile_number: '', comments: '' });
        } catch (err: any) {
            console.error('Failed to create order:', err);
            Swal.fire({
                title: 'Error',
                text: err?.response?.data?.message || 'Failed to create order',
                icon: 'error'
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl">
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold">Create New Order</h3>
                    {loading && <span className="text-sm text-gray-500">Loading data...</span>}
                </div>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* General Info */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-700 dark:text-gray-200 border-b pb-2">Customer Details</h4>
                        <div>
                            <label className="block text-sm font-medium mb-1">Customer</label>
                            <select
                                value={formData.user_id}
                                onChange={e => handleUserChange(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                required
                            >
                                <option value="">Select Customer</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.first_name ? `${u.first_name} ${u.last_name || ''}` : u.username || u.email} (#{u.id})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Email</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Phone</label>
                            <input
                                type="text"
                                value={formData.mobile_number}
                                onChange={e => setFormData({ ...formData, mobile_number: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                required
                            />
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-medium">Order Status</label>
                                <button
                                    type="button"
                                    onClick={() => setIsStatusManagerOpen(true)}
                                    className="text-xs text-brand-500 hover:text-brand-600 font-medium"
                                >
                                    Manage
                                </button>
                            </div>
                            <select
                                value={formData.status_id}
                                onChange={e => setFormData({ ...formData, status_id: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                required
                            >
                                <option value="">Select Status</option>
                                {statuses.map(s => (
                                    <option key={s.id} value={s.id}>{s.name || s.status}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Order Items */}
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-700 dark:text-gray-200 border-b pb-2">Order Items</h4>
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-1">Product</label>
                                <select
                                    value={selectedProduct}
                                    onChange={e => setSelectedProduct(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                >
                                    <option value="">Select Product</option>
                                    {products.map(p => (
                                        <option key={p.id} value={p.id}>{p.title || p.name} (${p.price})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="w-20">
                                <label className="block text-sm font-medium mb-1">Qty</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={qty}
                                    onChange={e => setQty(Number(e.target.value))}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={addItem}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                            >
                                Add
                            </button>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 min-h-[150px] max-h-[250px] overflow-y-auto">
                            {items.length === 0 ? (
                                <p className="text-gray-500 text-center text-sm py-4">No items added</p>
                            ) : (
                                <div className="space-y-2">
                                    {items.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center text-sm bg-white dark:bg-gray-700 p-2 rounded border dark:border-gray-600">
                                            <span>{item.productName} x {item.quantity}</span>
                                            <div className="flex items-center gap-3">
                                                <span>${(item.price * item.quantity).toFixed(2)}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(idx)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between items-center font-bold text-lg pt-2 border-t">
                            <span>Total Estimate:</span>
                            <span>${calculateTotal().toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="col-span-1 lg:col-span-2 flex justify-end gap-3 mt-6 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
                        >
                            {loading ? 'Processing...' : 'Create Order'}
                        </button>
                    </div>
                </form>

                {/* Status Manager Modal */}
                <OrderStatusManagerModal
                    isOpen={isStatusManagerOpen}
                    onClose={() => setIsStatusManagerOpen(false)}
                    onChange={() => fetchStatuses()}
                />
            </div>
        </Modal>
    );
}
